// Phase 11 Step 6 — useOwlHourSlips hook.
//
// Read-only consumer of owl_hour_slips table. Fetches tonight's slips
// across all five tiers plus recent history. Supports tier filtering.
//
// Trust contract:
//   - Read via RLS-scoped SELECT. Writes via upsert_owl_hour_slip RPC only.
//   - Defensive parsing — missing fields render with fallbacks.
//   - Static fallback slips when DB is empty (phantom demo).
//   - No localStorage. No external HTTP.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";
import { owlActiveRefreshPhase, owlSessionDate, type OwlRefreshPhase } from "./owlScreenState.ts";

// ─── Types ─────────────────────────────────────────────────────

export type OwlTier = "rollover" | "moderate" | "conservative" | "aggressive" | "owl_1000";
export type OwlSlipStatus = "draft" | "published" | "settled" | "void";
export type OwlResult = "win" | "loss" | "partial" | "void";

export interface OwlLeg {
  fixture_id: string;
  /** Clean team field (new pipeline payload). Optional for legacy slips. */
  home?: string;
  away?: string;
  /** Long-form league name (e.g. "Copa Libertadores"). Optional — may be
   *  absent on legacy slips; UI falls back to league_short. */
  league?: string;
  league_short: string;
  /** Legacy concatenated label "Home vs Away". Kept for backwards compat;
   *  consumers should prefer home/away when present. */
  match_name?: string;
  market: string;
  selection: string;
  odds: number;
  /** Substrate fair probability — used to derive favorite/underdog tags. */
  model_probability?: number;
  confidence: number;
  reasoning: string;
  kickoff_at: string;
  result: "win" | "loss" | "pending" | null;
  flags: {
    altitude?: boolean;
    rivalry?: boolean;
    home_adv?: number;
    travel_fatigue?: boolean;
    sample_warning?: boolean;
    turf?: boolean;
  };
}

export interface OwlHourSlip {
  id: string;
  tier: OwlTier;
  slip_date: string;
  refresh_phase?: OwlRefreshPhase;
  legs: readonly OwlLeg[];
  combined_odds: number | null;
  confidence: number | null;
  leagues_covered: readonly string[];
  continent_flags: {
    altitude?: boolean;
    rivalry?: boolean;
    sample_warning?: boolean;
    travel_fatigue?: boolean;
  };
  status: OwlSlipStatus;
  result: OwlResult | null;
  result_ready_at: string | null;
  notified_at: string | null;
  meta: Record<string, unknown>;
  simulated: boolean;
  created_at: string;
  updated_at: string;
}

export interface UseOwlHourSlipsState {
  slips: readonly OwlHourSlip[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// ─── Row parser ────────────────────────────────────────────────

function asStr(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}

function asNum(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function parseLeg(raw: unknown): OwlLeg {
  const r = (typeof raw === "object" && raw !== null ? raw : {}) as Record<string, unknown>;
  const homeStr = typeof r.home === "string" && r.home.length > 0 ? r.home : undefined;
  const awayStr = typeof r.away === "string" && r.away.length > 0 ? r.away : undefined;
  const leagueStr = typeof r.league === "string" && r.league.length > 0 ? r.league : undefined;
  const matchNameStr = typeof r.match_name === "string" && r.match_name.length > 0 ? r.match_name : undefined;
  const modelProb = r.model_probability != null && Number.isFinite(Number(r.model_probability))
    ? Number(r.model_probability) : undefined;
  return {
    fixture_id: String(r.fixture_id ?? ""),
    home: homeStr,
    away: awayStr,
    league: leagueStr,
    league_short: String(r.league_short ?? ""),
    match_name: matchNameStr,
    market: String(r.market ?? ""),
    selection: String(r.selection ?? ""),
    odds: typeof r.odds === "number" ? r.odds : Number(r.odds ?? 0),
    model_probability: modelProb,
    confidence: typeof r.confidence === "number" ? r.confidence : Number(r.confidence ?? 0),
    reasoning: String(r.reasoning ?? ""),
    kickoff_at: String(r.kickoff_at ?? ""),
    result: (r.result as OwlLeg["result"]) ?? null,
    flags: typeof r.flags === "object" && r.flags !== null
      ? (r.flags as OwlLeg["flags"])
      : {},
  };
}

function parseOwlHourSlip(row: Record<string, unknown>): OwlHourSlip {
  const rawLegs = Array.isArray(row.legs) ? row.legs : [];
  return {
    id: String(row.id ?? ""),
    tier: (row.tier as OwlTier) ?? "conservative",
    slip_date: String(row.slip_date ?? ""),
    refresh_phase: row.refresh_phase === "noon" ? "noon" : "evening",
    legs: rawLegs.map(parseLeg),
    combined_odds: asNum(row.combined_odds),
    confidence: asNum(row.confidence),
    leagues_covered: Array.isArray(row.leagues_covered)
      ? (row.leagues_covered as string[])
      : [],
    continent_flags:
      typeof row.continent_flags === "object" && row.continent_flags !== null
        ? (row.continent_flags as OwlHourSlip["continent_flags"])
        : {},
    status: (row.status as OwlSlipStatus) ?? "draft",
    result: asStr(row.result) as OwlResult | null,
    result_ready_at: asStr(row.result_ready_at),
    notified_at: asStr(row.notified_at),
    meta:
      typeof row.meta === "object" && row.meta !== null
        ? (row.meta as Record<string, unknown>)
        : {},
    simulated: row.simulated === true,
    created_at: String(row.created_at ?? ""),
    updated_at: String(row.updated_at ?? ""),
  };
}

// ─── Static fallback slips ────────────────────────────────────

function todayStr(): string {
  return owlSessionDate();
}

function makeDefaultSlips(): readonly OwlHourSlip[] {
  const today = todayStr();
  const base: Omit<OwlHourSlip, "id" | "tier" | "legs" | "combined_odds" | "confidence" | "leagues_covered"> = {
    slip_date: today,
    continent_flags: { rivalry: true },
    status: "published",
    result: null,
    result_ready_at: null,
    notified_at: null,
    meta: {},
    simulated: true,
    created_at: "",
    updated_at: "",
  };

  return [
    {
      ...base,
      id: "seed-owl-rollover",
      tier: "rollover",
      combined_odds: 3.2,
      confidence: 74,
      leagues_covered: ["BRA-A", "ARG-1"],
      legs: [
        {
          fixture_id: "f1",
          home: "Boca Juniors",
          away: "Independiente",
          league: "Argentine Primera Division",
          league_short: "ARG-1",
          market: "Match Result",
          selection: "Boca Juniors Win",
          odds: 1.65,
          confidence: 76,
          reasoning: "Boca unbeaten in 8 home games. Independiente 1 win in 6 away.",
          kickoff_at: "22:00",
          result: null,
          flags: { home_adv: 1.15 },
        },
        {
          fixture_id: "f2",
          home: "Santos",
          away: "Palmeiras",
          league: "Brazilian Serie A",
          league_short: "BRA-A",
          market: "Over/Under",
          selection: "Over 2.5 Goals",
          odds: 1.94,
          confidence: 72,
          reasoning: "Santos avg 3.1 goals at home. Palmeiras scored in 9 of 10 away.",
          kickoff_at: "23:30",
          result: null,
          flags: {},
        },
      ],
    },
    {
      ...base,
      id: "seed-owl-moderate",
      tier: "moderate",
      combined_odds: 5.4,
      confidence: 71,
      leagues_covered: ["MLS"],
      legs: [
        {
          fixture_id: "f3",
          home: "Inter Miami",
          away: "NYCFC",
          league: "Major League Soccer",
          league_short: "MLS",
          market: "Match Result",
          selection: "Inter Miami Win",
          odds: 2.10,
          confidence: 73,
          reasoning: "Miami 5 wins in last 6 at home. NYCFC travel fatigue from midweek.",
          kickoff_at: "23:30",
          result: null,
          flags: { home_adv: 1.10, turf: true },
        },
        {
          fixture_id: "f4",
          home: "LA Galaxy",
          away: "LAFC",
          league: "Major League Soccer",
          league_short: "MLS",
          market: "Both Teams to Score",
          selection: "BTTS Yes",
          odds: 1.72,
          confidence: 70,
          reasoning: "El Trafico rivalry — both teams scored in 7 of last 8 meetings.",
          kickoff_at: "01:00",
          result: null,
          flags: { rivalry: true, home_adv: 1.10 },
        },
        {
          fixture_id: "f5",
          home: "Cruz Azul",
          away: "Monterrey",
          league: "Liga MX",
          league_short: "LMX",
          market: "Over/Under",
          selection: "Over 2.5 Goals",
          odds: 1.50,
          confidence: 68,
          reasoning: "Cruz Azul avg 3.2 goals at Estadio Azul this season.",
          kickoff_at: "02:00",
          result: null,
          flags: { home_adv: 1.12 },
        },
      ],
    },
    {
      ...base,
      id: "seed-owl-conservative",
      tier: "conservative",
      combined_odds: 18.4,
      confidence: 64,
      leagues_covered: ["ARG-1", "BRA-A", "MLS", "LMX"],
      legs: [
        {
          fixture_id: "f1",
          home: "Boca Juniors",
          away: "Independiente",
          league: "Argentine Primera Division",
          league_short: "ARG-1",
          market: "Match Result",
          selection: "Boca Juniors Win",
          odds: 1.65,
          confidence: 76,
          reasoning: "Boca unbeaten in 8 home games. Independiente 1 win in 6 away.",
          kickoff_at: "22:00",
          result: null,
          flags: { home_adv: 1.15 },
        },
        {
          fixture_id: "f6",
          home: "Flamengo",
          away: "Corinthians",
          league: "Brazilian Serie A",
          league_short: "BRA-A",
          market: "Both Teams to Score",
          selection: "BTTS Yes",
          odds: 1.85,
          confidence: 69,
          reasoning: "Both teams scored in 8 of last 10 H2H at Maracana.",
          kickoff_at: "00:00",
          result: null,
          flags: {},
        },
        {
          fixture_id: "f3",
          home: "Inter Miami",
          away: "NYCFC",
          league: "Major League Soccer",
          league_short: "MLS",
          market: "Match Result",
          selection: "Inter Miami Win",
          odds: 2.10,
          confidence: 73,
          reasoning: "Miami 5 wins in last 6 at home. NYCFC travel fatigue from midweek.",
          kickoff_at: "23:30",
          result: null,
          flags: { home_adv: 1.10 },
        },
        {
          fixture_id: "f5",
          home: "Cruz Azul",
          away: "Monterrey",
          league: "Liga MX",
          league_short: "LMX",
          market: "Over/Under",
          selection: "Over 2.5 Goals",
          odds: 1.50,
          confidence: 68,
          reasoning: "Cruz Azul avg 3.2 goals at Estadio Azul this season.",
          kickoff_at: "02:00",
          result: null,
          flags: { home_adv: 1.12 },
        },
        {
          fixture_id: "f7",
          home: "Penarol",
          away: "Nacional",
          league: "Uruguayan Primera",
          league_short: "URU",
          market: "Match Result",
          selection: "Penarol Win",
          odds: 1.90,
          confidence: 61,
          reasoning: "Penarol 4 wins in last 5 at Campeon del Siglo. Rivalry match.",
          kickoff_at: "01:30",
          result: null,
          flags: { rivalry: true, home_adv: 1.15, sample_warning: true },
        },
      ],
    },
    {
      ...base,
      id: "seed-owl-aggressive",
      tier: "aggressive",
      combined_odds: 34.8,
      confidence: 57,
      leagues_covered: ["ARG-1", "BRA-A", "MLS", "LMX", "LIBER"],
      legs: [
        {
          fixture_id: "f1",
          home: "Boca Juniors",
          away: "Independiente",
          league: "Argentine Primera Division",
          league_short: "ARG-1",
          market: "Match Result",
          selection: "Boca Juniors Win",
          odds: 1.65,
          confidence: 76,
          reasoning: "Boca unbeaten in 8 home games. Independiente 1 win in 6 away.",
          kickoff_at: "22:00",
          result: null,
          flags: { home_adv: 1.15 },
        },
        {
          fixture_id: "f2",
          home: "Santos",
          away: "Palmeiras",
          league: "Brazilian Serie A",
          league_short: "BRA-A",
          market: "Over/Under",
          selection: "Over 2.5 Goals",
          odds: 1.94,
          confidence: 72,
          reasoning: "Santos avg 3.1 goals at home. Palmeiras scored in 9 of 10 away.",
          kickoff_at: "23:30",
          result: null,
          flags: {},
        },
        {
          fixture_id: "f6",
          home: "Flamengo",
          away: "Corinthians",
          league: "Brazilian Serie A",
          league_short: "BRA-A",
          market: "Both Teams to Score",
          selection: "BTTS Yes",
          odds: 1.85,
          confidence: 69,
          reasoning: "Both teams scored in 8 of last 10 H2H at Maracana.",
          kickoff_at: "00:00",
          result: null,
          flags: {},
        },
        {
          fixture_id: "f3",
          home: "Inter Miami",
          away: "NYCFC",
          league: "Major League Soccer",
          league_short: "MLS",
          market: "Match Result",
          selection: "Inter Miami Win",
          odds: 2.10,
          confidence: 73,
          reasoning: "Miami 5 wins in last 6 at home. NYCFC travel fatigue from midweek.",
          kickoff_at: "23:30",
          result: null,
          flags: { home_adv: 1.10 },
        },
        {
          fixture_id: "f5",
          home: "Cruz Azul",
          away: "Monterrey",
          league: "Liga MX",
          league_short: "LMX",
          market: "Over/Under",
          selection: "Over 2.5 Goals",
          odds: 1.50,
          confidence: 68,
          reasoning: "Cruz Azul avg 3.2 goals at Estadio Azul this season.",
          kickoff_at: "02:00",
          result: null,
          flags: { home_adv: 1.12 },
        },
        {
          fixture_id: "f7",
          home: "Penarol",
          away: "Nacional",
          league: "Uruguayan Primera",
          league_short: "URU",
          market: "Match Result",
          selection: "Penarol Win",
          odds: 1.90,
          confidence: 61,
          reasoning: "Penarol 4 wins in last 5. El Clasico del Rio de la Plata.",
          kickoff_at: "01:30",
          result: null,
          flags: { rivalry: true, home_adv: 1.15, sample_warning: true },
        },
        {
          fixture_id: "f8",
          home: "River Plate",
          away: "Racing Club",
          league: "Copa Libertadores",
          league_short: "LIBER",
          market: "Match Result",
          selection: "River Plate Win",
          odds: 1.55,
          confidence: 65,
          reasoning: "River dominant at Monumental in Libertadores. 6W 1D last 7 home.",
          kickoff_at: "22:00",
          result: null,
          flags: { home_adv: 1.15 },
        },
      ],
    },
  ];
}

export const DEFAULT_OWL_SLIPS: readonly OwlHourSlip[] = makeDefaultSlips();

// ─── Tier metadata ────────────────────────────────────────────

export interface OwlTierMeta {
  label: string;
  name: string;
  subtitle: string;
  oddsRange: string;
  legRange: string;
  minConfidence: number;
  defaultStake: number;
}

export const OWL_TIER_META: Record<OwlTier, OwlTierMeta> = {
  rollover: {
    label: "ROLLOVER",
    name: "Owl Hour Rollover",
    subtitle: "Compound while you sleep",
    oddsRange: "2.0× – 5.0×",
    legRange: "1–2 legs",
    minConfidence: 70,
    defaultStake: 50,
  },
  moderate: {
    label: "MODERATE",
    name: "Moderate Scout",
    subtitle: "Best value pick tonight",
    oddsRange: "3.0× – 8.0×",
    legRange: "1–3 legs",
    minConfidence: 70,
    defaultStake: 25,
  },
  conservative: {
    label: "CONSERVATIVE ACCA",
    name: "Conservative Acca",
    subtitle: "Sleep-to-win accumulator",
    oddsRange: "10× – 30×",
    legRange: "4–6 legs",
    minConfidence: 60,
    defaultStake: 10,
  },
  aggressive: {
    label: "AGGRESSIVE ACCA",
    name: "Aggressive Acca",
    subtitle: "Dream slip",
    oddsRange: "30× – 40×",
    legRange: "6–9 legs",
    minConfidence: 55,
    defaultStake: 5,
  },
  owl_1000: {
    label: "OWL 1000",
    name: "Owl 1000",
    subtitle: "The nightly flagship",
    oddsRange: "1,000× – 2,500×",
    legRange: "12–16 legs",
    minConfidence: 40,
    defaultStake: 2,
  },
};

// ─── Hook ──────────────────────────────────────────────────────

export function useOwlHourSlips(
  opts: {
    tierFilter?: OwlTier;
  } = {},
): UseOwlHourSlipsState {
  const { user } = useAuthSession();
  const [slips, setSlips] = useState<readonly OwlHourSlip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [activePhase, setActivePhase] = useState(() => owlActiveRefreshPhase());

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      const nextPhase = owlActiveRefreshPhase();
      setActivePhase((current) => current === nextPhase ? current : nextPhase);
      refetch();
    }, 30_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!user) {
      setSlips([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const client = getBrowserClient();
    setLoading(true);
    setError(null);

    void (async () => {
      try {
      // OWL HOUR doctrine §5/§11: tonight is tonight. Never fall back
      // to yesterday's slips when today is honest-empty. The UI must
      // see "STRIKZA stayed out tonight" not "here's last night's pick
      // dressed up as live data". Strict slip_date = today filter.
      const today = todayStr();

      const resp = await (client.rpc as any)("get_owl_publications", { p_since: today, p_until: today });
      if (cancelled) return;

      if (resp.error) {
        setSlips((previous) => previous.filter((slip) => slip.slip_date === today));
        setError(resp.error.message);
        setLoading(false);
        return;
      }

      const rows: Record<string, unknown>[] = Array.isArray(resp.data) ? resp.data : [];
      const parsed = rows.filter((row) => row.status === "published")
        .filter((row) => !opts.tierFilter || row.tier === opts.tierFilter)
        .filter((row) => row.refresh_phase === activePhase ||
          (activePhase === "evening" && row.refresh_phase === "noon" &&
            !rows.some((other) => other.tier === row.tier && other.refresh_phase === "evening" && other.status === "published")))
        .map(parseOwlHourSlip);
      setSlips(parsed);
      setLoading(false);
      } catch (failure) {
        if (cancelled) return;
        setSlips((previous) => previous.filter((slip) => slip.slip_date === todayStr()));
        setError(failure instanceof Error ? failure.message : "Publication unavailable");
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, opts.tierFilter, tick, activePhase]);

  return useMemo(
    () => ({ slips, loading, error, refetch }),
    [slips, loading, error, refetch],
  );
}
